import { FlaskConical } from 'lucide-react'
import clsx from 'clsx'
import { describePreflightItem, stageStatusLabel, type PreflightLine } from '@/utils/patchStage'
import type { DryRunResult } from '@/types/patching'
import styles from './Patching.module.css'

function summaryText(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value)
  return JSON.stringify(value)
}

function verdict(passed: boolean | null): string {
  return passed === true ? 'PASS' : passed === false ? 'FAIL' : 'INFO'
}

function lineClass(passed: boolean | null): string | undefined {
  return passed === true ? styles.checkPass : passed === false ? styles.checkFail : undefined
}

function isUninvokedInstall(detail: string | null): boolean {
  const text = (detail || '').toLowerCase()
  return text.includes('installpatches was not invoked') || text.includes('live azure execution is disabled') || text.includes('not confirmed')
}

/** A dry run is not an install failure. Keep real pre-flight failures visible. */
function softenDryRun(item: PreflightLine): PreflightLine {
  const checks = item.checks.map((check) =>
    check.label === 'azure_install' && check.passed === false && isUninvokedInstall(check.detail)
      ? { ...check, passed: null, mandatory: false, detail: 'not called on a dry run' }
      : check,
  )
  const detail = isUninvokedInstall(item.detail) ? 'Dry run — nothing was installed.' : item.detail
  return { ...item, checks, detail }
}

function PreflightItem({ item }: { item: PreflightLine }) {
  return (
    <li className={lineClass(item.passed)} data-passed={item.passed === null ? 'unknown' : String(item.passed)}>
      {verdict(item.passed)} · <span className={styles.mono}>{item.label}</span>
      {item.detail ? ` — ${item.detail}` : ''}
      {item.checks.length > 0 && (
        <ul className={styles.checkList} style={{ marginLeft: 16 }}>
          {item.checks.map((check, i) => (
            <li key={`${i}-${check.label}`} className={lineClass(check.passed)} data-passed={check.passed === null ? 'unknown' : String(check.passed)}>
              {verdict(check.passed)} · <span className={styles.mono}>{check.label}</span>
              {check.mandatory === null ? '' : check.mandatory ? ' (mandatory)' : ' (optional)'}
              {check.detail ? ` — ${check.detail}` : ''}
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

/**
 * Outcome of a dry run (contract §11.1). A dry run installs nothing and can never satisfy promotion.
 * If it passed the stage is back at Approved; if it did not, the stage failed — the wording follows
 * the portal's own `passed` flag and the stage's real status, never an assumption.
 */
export function DryRunPanel({ result, status }: { result: DryRunResult | null | undefined; status: string }) {
  if (!result) return null
  const items = (result.preflight ?? []).map(describePreflightItem).map(softenDryRun)
  const summary = Object.entries(result.summary ?? {})
  const problems = result.problems ?? []
  const failed = result.passed === false
  return (
    <section className={styles.section} aria-label="Dry run result" data-testid="dry-run-result" data-passed={failed ? 'false' : 'true'}>
      <h3 className={styles.sectionTitle}>
        <FlaskConical size={14} /> Dry run result
      </h3>
      <div className={styles.stack}>
        <div className={clsx(styles.alert, failed ? styles.alertError : styles.alertInfo)}>
          <span>
            {failed ? (
              <>
                <strong>The dry run did not pass.</strong> Pre-flight or assessment found a problem.{' '}
              </>
            ) : (
              <>
                This was a <strong>dry run</strong>: pre-flight and assessment only.{' '}
              </>
            )}
            Nothing was installed and it does not count towards acceptance.
            {status === 'approved' ? ' The stage is back at Approved.' : ` The stage is ${stageStatusLabel(status)}.`}
          </span>
        </div>
        {problems.length > 0 && (
          <ul className={styles.advisorList} data-testid="dry-run-problems">
            {problems.map((problem, i) => (
              <li key={`${i}-${problem}`}>{problem}</li>
            ))}
          </ul>
        )}
        {items.length === 0 ? (
          <p className={styles.muted}>No pre-flight checks were recorded.</p>
        ) : (
          <ul className={styles.checkList} data-testid="dry-run-preflight">
            {items.map((item, i) => (
              <PreflightItem key={`${i}-${item.label}`} item={item} />
            ))}
          </ul>
        )}
        {summary.length > 0 && (
          <dl className={styles.factGrid} data-testid="dry-run-summary">
            {summary.map(([key, value]) => (
              <div key={key}>
                <dt className={styles.factLabel}>{key.replace(/_/g, ' ')}</dt>
                <dd className={styles.factValue}>{summaryText(value)}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </section>
  )
}
